// Compact card grid for Browse: topic pill, status/type pill, title, a short
// summary in the selected lens, source and relative time. Loads more as you
// scroll (with a button fallback).
import { useEffect, useRef } from "react";
import { CONTENT_TYPE_LABELS, LENS_LABELS, TOPIC_LABELS, type Lens, type Story } from "../../shared/model.ts";
import { effectiveFlag } from "../../shared/flags.ts";
import { FlagChip } from "./FlagChip.tsx";
import { shortSource } from "../lib/sourceLabel.ts";
import { storyColor, type ColorBy } from "../lib/colorKeys.ts";
import { onLinkClick } from "../hooks/useRouter.ts";
import { formatDate } from "../lib/briefing.ts";
import { relativeTime } from "../lib/relativeTime.ts";
import { CardActions, btn, TypeIcon } from "./ui.tsx";
import { SummaryText } from "./SummaryText.tsx";

interface Props {
  stories: Story[];
  lens: Lens;
  isSaved(id: string): boolean;
  onToggleSave(id: string): void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore(): void;
  /** Color-code cards by topic or type (a stripe and dots; labels stay text). */
  colorBy?: ColorBy;
}

export function GridView({ stories, lens, isSaved, onToggleSave, hasMore, loadingMore, onLoadMore, colorBy = "off" }: Props) {
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && !loadingMore && onLoadMore(), { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore, stories.length]);

  return (
    <div className="@container px-4 sm:px-6 lg:px-8">
      {/* Columns follow the available width (container query), so opening the
          Basket panel drops to two columns instead of squeezing three. */}
      <ul aria-label="Updates" className="grid gap-3 @2xl:grid-cols-2 @6xl:grid-cols-3">
        {stories.map((s) => (
          <GridCard key={s.id} story={s} lens={lens} saved={isSaved(s.id)} onToggleSave={() => onToggleSave(s.id)} colorBy={colorBy} />
        ))}
      </ul>
      <div ref={sentinel} className="mt-5 text-center">
        {hasMore ? (
          <button type="button" className={btn.secondary} onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more updates"}
          </button>
        ) : (
          stories.length > 0 && <p className="text-xs text-ink-soft">That's every update matching these filters.</p>
        )}
      </div>
    </div>
  );
}

function GridCard({ story: s, lens, saved, onToggleSave, colorBy }: { story: Story; lens: Lens; saved: boolean; onToggleSave(): void; colorBy: ColorBy }) {
  const flag = effectiveFlag(s);
  const color = storyColor(colorBy, s);
  const date = s.publishedAt ?? s.sortAt;
  const headingId = `grid-${s.id}`;
  const unreviewed = s.summaryProvenance === "source_excerpt";
  return (
    <li
      aria-labelledby={headingId}
      className="group relative flex min-h-[13.5rem] flex-col rounded-xl border border-line bg-white p-4 transition-colors hover:border-line-strong focus-within:border-line-strong"
    >
      {/* Color key stripe (decorative; the topic tag and source line name the category). */}
      {color && <span aria-hidden="true" className="absolute inset-y-4 left-0 w-1 rounded-r" style={{ backgroundColor: color }} />}
      {/* Only what changes how you read it sits above the title: topic, status, fictional sample. */}
      <div className="flex min-h-7 min-w-0 flex-wrap items-center gap-1.5 pr-24">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-sand px-2 py-0.5 text-2xs font-semibold text-ink-soft">
          {colorBy === "topic" && <span aria-hidden="true" className="size-2 rounded-full" style={{ backgroundColor: color! }} />}
          {TOPIC_LABELS[s.primaryTopic]}
        </span>
        {flag && <FlagChip status={flag.status} size="md" />}
        {s.isSample && (
          <span className="rounded-md border border-dashed border-coral-deep/70 px-2 py-0.5 text-2xs font-semibold uppercase tracking-[0.06em] text-coral-deep">
            Fictional sample
          </span>
        )}
      </div>

      <h3 id={headingId} className="mt-2.5 line-clamp-2 text-base font-semibold leading-snug tracking-[-0.01em] decoration-line-strong underline-offset-2 group-hover:underline">
        {/* Stretched link: the whole block opens the story page. */}
        <a href={`/story/${s.id}`} onClick={onLinkClick} className="after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:outline-none">
          {s.title}
        </a>
      </h3>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-ink-soft">
        <span className="sr-only">{unreviewed ? "From the source: " : `${LENS_LABELS[lens]} summary: `}</span>
        <SummaryText text={s.summaries[lens]} compact />
      </p>
      <div className="mt-auto pt-4 text-xs text-ink-soft">
        <p className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="inline-flex" style={colorBy === "type" ? { color: color! } : undefined}>
              <TypeIcon type={s.contentType} label={CONTENT_TYPE_LABELS[s.contentType]} />
            </span>
            <span className="truncate">{shortSource(s.sourceName, s.contentType)}</span>
          </span>
          <time dateTime={new Date(date).toISOString()} title={formatDate(date)} className="shrink-0">
            {relativeTime(date)}
          </time>
        </p>
      </div>

      {/* Above the stretched link so they stay independently clickable; 44 × 44 targets. */}
      <CardActions storyId={s.id} title={s.title} url={s.canonicalUrl} saved={saved} onToggleSave={onToggleSave} className="absolute right-1.5 top-1.5 z-10" />
    </li>
  );
}
